import React from 'react';
import { useNavigate } from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';

import { updateSingleInvoice } from 'api/Invoice';

import {
  useInvoiceContext,
  useInvoiceEditContext
} from 'lib/context/InvoiceContext/InvoiceContext';
import { cn, iaRa } from 'lib/helpers/utility';
import { useCustomMutation } from 'lib/hooks/useCustomMutation';

import Button from 'components/shared/Button/Button';

import EditInvoiceModal from '../../EditInvoiceModal/EditInvoiceModal';
import { removeTempIdFromInvoice } from '../../EditInvoiceModal/lib/tempIdHandler';

const InvoiceEditButton = ({ preview, openModal, invoiceId, variant }) => {
  const navigate = useNavigate();
  const { setInvoices } = useInvoiceContext();
  const { editedInvoice, setEditedInvoice } = useInvoiceEditContext();
  const queryClient = useQueryClient();

  const { mutateAsync } = useCustomMutation({
    mutationFn: () =>
      updateSingleInvoice(navigate, { invoice: removeTempIdFromInvoice(editedInvoice) }),
    successMessage: 'Invoice updated successfully',
    errorMessage: 'Failed to update invoice',
    onSuccess: () => {
      setInvoices((prev) =>
        iaRa(prev).map((_invoice) => {
          if (_invoice.id === editedInvoice.id) {
            return editedInvoice;
          }
          return _invoice;
        })
      );
      setEditedInvoice(null);
      queryClient.invalidateQueries({ queryKey: ['internal_invoices', invoiceId] });
    },
    handleResponse: (res) => {
      if (res?.code !== 0) throw Error(res?.error);
    }
  });

  const handleSaveChanges = () => mutateAsync();

  if (preview) return null;

  const isPrimary = variant === 'primary';

  return (
    <Button
      variant="link"
      size="small"
      icon="edit-3-mono"
      iconSize={16}
      iconColor="neutral"
      className={cn('h-6 w-6 hover:bg-neutral-200', isPrimary && 'hover:bg-primary-100')}
      onClick={() => {
        openModal({
          title: `Invoice #${invoiceId}`,
          submitText: 'Save changes',
          onSubmit: handleSaveChanges,
          content: (props) => <EditInvoiceModal invoiceId={invoiceId} {...props} />
        });
      }}
    />
  );
};

export default InvoiceEditButton;
